<template>
  <div class="card-form-component">
    <StripeElements
      :ref="STRIPE_FIELDS.GROUP"
      v-slot="{ elements }"
      :stripe-key="stripeKey"
      :instance-options="instanceOptions"
      :elements-options="elementsOptions"
    >
      <QField
        stack-label
        :label="$t('ETAB.FORMS.LABELS.@CARD_NUMBER')"
        :error-message="getError(STRIPE_FIELDS.CARD_NUMBER)"
        :error="hasError(STRIPE_FIELDS.CARD_NUMBER)"
      >
        <template #control>
          <StripeElement
            :ref="STRIPE_FIELDS.CARD_NUMBER"
            type="cardNumber"
            :elements="elements"
            :options="cardNumberOptions"
            @change="onChange"
            @ready="getAndEmitStripeInstances"
          />
        </template>
      </QField>
      <div class="card-additional-info">
        <QField
          stack-label
          :error-message="getError(STRIPE_FIELDS.CARD_EXPIRY)"
          :error="hasError(STRIPE_FIELDS.CARD_EXPIRY)"
          class="card-expiry"
          :label="$t('ETAB.FORMS.LABELS.@EXPIRATION_DATE')"
        >
          <template #control>
            <StripeElement :ref="STRIPE_FIELDS.CARD_EXPIRY" type="cardExpiry" :elements="elements" :options="cardExpiryOptions" @change="onChange" />
          </template>
        </QField>

        <QField
          stack-label
          :error-message="getError(STRIPE_FIELDS.CARD_CVC)"
          :error="hasError(STRIPE_FIELDS.CARD_CVC)"
          :hint="$t('ETAB.FORMS.LABELS.@CCV_HELPER')"
          class="card-cvc"
          :label="$t('ETAB.FORMS.LABELS.@CCV')"
        >
          <template #control>
            <StripeElement :ref="STRIPE_FIELDS.CARD_CVC" type="cardCvc" :elements="elements" :options="cardCvcOptions" @change="onChange" />
          </template>
        </QField>
      </div>
    </StripeElements>
  </div>
</template>
<script>
  import { StripeElements, StripeElement } from "vue-stripe-js";
  import CoreColors from "@core/styles/core.variables.json";
  import { STRIPE_PK, STRIPE_FIELDS } from "@core/constants";

  export default {
    components: {
      StripeElements,
      StripeElement,
    },
    emits: ["referenceStripeInstances"],
    data() {
      return {
        stripeKey: STRIPE_PK,
        fields: {
          [STRIPE_FIELDS.CARD_NUMBER]: {},
          [STRIPE_FIELDS.CARD_EXPIRY]: {},
          [STRIPE_FIELDS.CARD_CVC]: {},
        },
        instanceOptions: {
          // https://stripe.com/docs/js/initializing#init_stripe_js-options
          locale: "fr",
        },
        elementsOptions: {
          // https://stripe.com/docs/js/elements_object/create#stripe_elements-options
        },
        stripeOptions: {
          style: {
            base: { fontFamily: "Arial" },
            invalid: {
              color: CoreColors.negative,
              ":focus": {
                color: CoreColors.negative,
              },
            },
          },
        },
      };
    },
    created() {
      this.STRIPE_FIELDS = STRIPE_FIELDS;
      this.initStripeOptions();
    },
    unmounted() {
      document.querySelectorAll("iframe").forEach((iframe) => {
        if (iframe.getAttribute("src")?.includes("stripe")) {
          iframe.parentNode.removeChild(iframe);
        }
      });
    },
    methods: {
      initStripeOptions() {
        this.cardNumberOptions = {
          ...this.fieldsOptions,
          placeholder: "XXXX XXXX XXXX XXXX",
          showIcon: true,
          style: { base: { fontSize: "15px" } },
          preferredNetwork: ["visa", "mastercard"],
        };
        this.cardExpiryOptions = { ...this.fieldsOptions, placeholder: "MM / AA" };
        this.cardCvcOptions = { ...this.fieldsOptions, placeholder: "000" };
      },
      getAndEmitStripeInstances() {
        // we need to send Stripe instance and Stripe CardNumber element
        // careful, the Stripe CardNumber element is available after a little delay
        // (hence the @ready method)
        // Stripe instance is hidden in the StripeElements component
        // https://github.com/ectoflow/vue-stripe-elements#3-use-built-in-components
        const stripeInstance = this.$refs[STRIPE_FIELDS.GROUP]?.instance;
        // Stripe CardNumber element is in here as well
        const cardStripeElement = this.$refs[STRIPE_FIELDS.CARD_NUMBER]?.stripeElement;
        this.$emit("referenceStripeInstances", { stripeInstance, cardStripeElement });
      },
      handleFocus(type) {
        // we allow for autofocus to the next invalid field
        switch (type) {
          case STRIPE_FIELDS.CARD_NUMBER: {
            // from CARD_NUMBER we can go to any of the other two fields
            if (this.isValid(type)) {
              if (!this.isValid(STRIPE_FIELDS.CARD_EXPIRY)) {
                // we find the input in the stripe iframe of the wanted element
                const nextInput = this.$refs[STRIPE_FIELDS.CARD_EXPIRY].$el.querySelector("input");
                nextInput.focus();
              } else if (!this.isValid(STRIPE_FIELDS.CARD_CVC)) {
                const nextInput = this.$refs[STRIPE_FIELDS.CARD_CVC].$el.querySelector("input");
                nextInput.focus();
              }
            }
            break;
          }
          case STRIPE_FIELDS.CARD_EXPIRY: {
            // from CARD_EXPIRY we can only go to the CARD_CVC
            if (this.isValid(type)) {
              if (!this.isValid(STRIPE_FIELDS.CARD_CVC)) {
                const nextInput = this.$refs[STRIPE_FIELDS.CARD_CVC].$el.querySelector("input");
                nextInput.focus();
              }
            }
            break;
          }
          default:
            // nothing
            break;
        }
      },
      getError(type) {
        return this.fields[type].error?.message;
      },
      hasError(type) {
        return this.fields[type].error !== undefined;
      },
      isValid(type) {
        return this.fields[type].complete === true;
      },
      onChange(event) {
        if (event) {
          const type = event.elementType;
          // we save the event
          this.fields[type] = event;
          this.handleFocus(type);
        }
      },
    },
  };
</script>
<style lang="scss">
  .card-form-component {
    .StripeElement {
      min-width: 250px; // for some reason the width is 0 by default because Stripe elements thinks it's empty
    }

    .card-additional-info {
      display: flex;

      .card-expiry {
        flex: 1;
        .StripeElement {
          min-width: 125px; // for some reason the width is 0 by default because Stripe elements thinks it's empty
        }
      }

      .card-cvc {
        flex: 1;
        margin-left: 15px;
        .StripeElement {
          min-width: 125px; // for some reason the width is 0 by default because Stripe elements thinks it's empty
        }
      }
    }

    .q-field {
      margin-bottom: 25px;
    }
  }
</style>
